import { useState } from 'react'
import { Brain, Image, Search, Pencil } from 'lucide-react'
import { useAppStore } from '../../stores/app'
import { useAgentConfigs } from '../../hooks/useAdminConfig'
import type { AgentConfigListItem } from '../../lib/api'
import { AgentEditModal } from './AgentEditModal'
import { LoadingSpinner } from '../ui/LoadingSpinner'

const TEAM_ORDER = ['content_team', 'visual_team', 'strategy_team'] as const
const TEAM_META: Record<string, { label: string; icon: typeof Brain }> = {
  content_team: { label: 'Content Team', icon: Brain },
  visual_team: { label: 'Visual Team', icon: Image },
  strategy_team: { label: 'Strategy Team', icon: Search },
  content: { label: 'Content Team', icon: Brain },
  visual: { label: 'Visual Team', icon: Image },
  strategy: { label: 'Strategy Team', icon: Search },
}

function groupAgentsByTeam(agents: AgentConfigListItem[]): Map<string, AgentConfigListItem[]> {
  const groups = new Map<string, AgentConfigListItem[]>()
  for (const agent of agents) {
    const team = agent.team || 'other'
    const normalizedTeam = team === 'content' || team === 'visual' || team === 'strategy' ? `${team}_team` : team
    if (!groups.has(normalizedTeam)) groups.set(normalizedTeam, [])
    groups.get(normalizedTeam)!.push(agent)
  }
  for (const key of TEAM_ORDER) {
    if (!groups.has(key)) groups.set(key, [])
  }
  return groups
}

export function SettingsAgentsSection() {
  const organizationId = useAppStore((state) => state.organizationId)
  const { data: agentsRaw = [], isLoading } = useAgentConfigs(organizationId ?? undefined)
  const agents: AgentConfigListItem[] = Array.isArray(agentsRaw) ? agentsRaw : []
  const [editingAgent, setEditingAgent] = useState<AgentConfigListItem | null>(null)

  const grouped = groupAgentsByTeam(agents)

  if (isLoading) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-800">Agentes</h2>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </section>
    )
  }

  return (
    <>
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-800">Agentes</h2>
        <p className="text-sm text-slate-500 mt-1">Configuracion por agente (prompt, modelo, temperatura).</p>

        <div className="mt-6 space-y-6">
          {TEAM_ORDER.map((teamKey) => {
            const teamAgents = grouped.get(teamKey) ?? []
            if (teamAgents.length === 0) return null
            const meta = TEAM_META[teamKey] ?? { label: teamKey, icon: Brain }
            const Icon = meta.icon
            return (
              <div key={teamKey}>
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-slate-400" />
                  {meta.label}
                </h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {teamAgents.map((agent) => (
                    <div
                      key={agent.agent_id}
                      className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/50 p-4"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{agent.display_name}</p>
                        <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                          {meta.label}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingAgent(agent)}
                        className="btn btn-secondary shrink-0 ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Editar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <AgentEditModal
        isOpen={editingAgent !== null}
        onClose={() => setEditingAgent(null)}
        agent={editingAgent}
        orgId={organizationId ?? ''}
        onSaved={() => setEditingAgent(null)}
      />
    </>
  )
}
